import React, { useEffect } from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  Divider,
  IconButton,
  useTheme,
  Dialog,
  DialogContent,
  DialogActions,
  Grid2,
  Chip,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  Close as CloseIcon,
  AssignmentReturn as AssignmentReturnIcon,
} from "@mui/icons-material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import AppSearchDropDown2 from "../../../../../../views/components/common/filter/AppSearchDropdown2";
import CustomTextField from "../../../../../../views/components/common/textFields/TextField";
import { useStores } from "../../../../use-stores";
import { FilterValue } from "../../../../../../models/Filters/FilterValue";
import { useNotification } from "../../../../../../utils/NotificationsContext";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import {
  SwageazyInventorySampleReturnRequestFormData,
  SwageazyInventorySkuLineItem,
  SampleSirReturnOption,
} from "../../../../../../models/SwageazyInventory/SwageazyInventory";
import { Warehouse } from "../../../../../../models/Warehouse/Warehouse";

interface SwageazyInventorySampleReturnProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  /** When set, order and SIR are fixed (e.g. opened from SIR detail page). */
  prefill?: {
    orderId: number;
    stockIssueRequestId: number;
    lockSelection?: boolean;
  };
}

interface SkuProductGroup {
  productId: number;
  productName: string;
  items: SwageazyInventorySkuLineItem[];
}

const groupSkuItemsByProduct = (
  items: SwageazyInventorySkuLineItem[],
): SkuProductGroup[] => {
  const groups = new Map<number, SkuProductGroup>();
  items.forEach((item) => {
    const existing = groups.get(item.product_id);
    if (existing) {
      existing.items.push(item);
    } else {
      groups.set(item.product_id, {
        productId: item.product_id,
        productName: item.product_name,
        items: [item],
      });
    }
  });
  return Array.from(groups.values());
};

const SwageazyInventorySampleReturn: React.FC<
  SwageazyInventorySampleReturnProps
> = ({ open, onClose, onSave, prefill }) => {
  const theme = useTheme();
  const { warehouseStore, swageazyInventoryStore, orderStore } = useStores();
  const { showError } = useNotification();

  useEffect(() => {
    if (!open) return;

    handleWarehouseSearch();

    if (prefill?.lockSelection) {
      swageazyInventoryStore
        .initSampleReturnFromSir(prefill.orderId, prefill.stockIssueRequestId)
        .catch((err: any) => {
          showError(
            "Error",
            err?.response?.data?.message ||
              "Failed to load return SKUs for this stock issue request.",
          );
        });
      return;
    }

    if (!swageazyInventoryStore.sampleReturnRequestFormData) {
      swageazyInventoryStore.setSampleReturnRequestFormData({
        sku_items: [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefill?.orderId, prefill?.stockIssueRequestId, prefill?.lockSelection]);

  useEffect(() => {
    if (!open) {
      swageazyInventoryStore.resetSampleReturnRequestFormData();
      if (swageazyInventoryStore.apiTimer) {
        clearTimeout(swageazyInventoryStore.apiTimer);
        swageazyInventoryStore.clearApiTimer();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    return () => {
      swageazyInventoryStore.clearApiTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOrderSearch = (searchText?: string) => {
    swageazyInventoryStore.clearApiTimer();
    if (searchText && searchText.length > 0) {
      const timer = setTimeout(() => {
        orderStore
          .searchOrderIds(searchText)
          .then(() => {
            const orderIds = orderStore.orderIds || [];
            const searchResults = orderIds.map((orderId) => ({
              id: String(orderId),
              value: String(orderId),
            }));
            swageazyInventoryStore.setOrderSearchResults(searchResults);
          })
          .catch((err: any) => {
            showError(
              "Error",
              err?.response?.data?.message ||
                "Something went wrong! Please try again later.",
            );
          });
      }, 800);
      swageazyInventoryStore.setApiTimer(timer);
    } else {
      swageazyInventoryStore.setOrderSearchResults([]);
    }
  };

  const handleOrderSelect = (selected: FilterValue[]) => {
    if (selected?.length > 0) {
      const orderId = Number(selected[0]?.id);
      if (!isNaN(orderId) && orderId > 0) {
        swageazyInventoryStore.setSampleReturnRequestFormData({
          order_id: orderId,
          stock_issue_request_id: undefined,
          sku_items: [],
          warehouse_id: undefined,
          warehouse_name: undefined,
          current_rack: undefined,
          box_id: undefined,
        });

        swageazyInventoryStore
          .loadSampleReturnSirOptions(orderId)
          .then((sirs) => {
            if (sirs.length <= 1) {
              const sirId =
                sirs.length === 1
                  ? sirs[0]!.id
                  : swageazyInventoryStore.sampleReturnRequestFormData
                      ?.stock_issue_request_id;
              return swageazyInventoryStore.loadSampleReturnSkuItemsFromOrder(
                orderId,
                sirId,
              );
            }
          })
          .catch((err: any) => {
            swageazyInventoryStore.setSampleReturnRequestFormData({
              sku_items: [],
            });
            showError(
              "Error",
              err?.response?.data?.message ||
                "Failed to fetch the order SKUs. Please try again.",
            );
          });
      }
    } else {
      swageazyInventoryStore.setSampleReturnRequestFormData({
        order_id: undefined,
        stock_issue_request_id: undefined,
        sku_items: [],
        warehouse_id: undefined,
        warehouse_name: undefined,
        current_rack: undefined,
        box_id: undefined,
      });
      swageazyInventoryStore.sampleReturnSirOptions = [];
    }
  };

  const handleSirSelect = (selected: FilterValue[]) => {
    const orderId =
      swageazyInventoryStore.sampleReturnRequestFormData?.order_id;
    if (!orderId) return;

    if (selected?.length > 0) {
      const sirId = Number(selected[0]?.id);
      if (!isNaN(sirId) && sirId > 0) {
        swageazyInventoryStore.setSampleReturnRequestFormData({
          stock_issue_request_id: sirId,
          sku_items: [],
        });
        swageazyInventoryStore
          .loadSampleReturnSkuItemsFromOrder(orderId, sirId)
          .catch((err: any) => {
            showError(
              "Error",
              err?.response?.data?.message ||
                "Failed to load SKUs for the selected stock issue request.",
            );
          });
      }
    } else {
      swageazyInventoryStore.setSampleReturnRequestFormData({
        stock_issue_request_id: undefined,
        sku_items: [],
      });
    }
  };

  const handleWarehouseSearch = () => {
    if (warehouseStore.warehouses.length === 0) {
      warehouseStore.searchWarehouse().catch(() => {});
    }
  };

  const handleWarehouseRackSearch = (
    warehouseId: string | number | undefined,
  ) => {
    if (warehouseId) {
      warehouseStore
        .searchWarehouseRack(String(warehouseId), "")
        .then(() => {
          if (warehouseStore.warehouseRacks?.length === 1) {
            const autoSelectedRackId =
              warehouseStore.warehouseRacks[0]?.id || "";
            swageazyInventoryStore.setSampleReturnRequestFormData({
              current_rack: autoSelectedRackId,
              box_id: undefined,
            });
          } else {
            swageazyInventoryStore.setSampleReturnRequestFormData({
              current_rack: undefined,
              box_id: undefined,
            });
          }
        })
        .catch(() => {
          swageazyInventoryStore.setSampleReturnRequestFormData({
            current_rack: undefined,
            box_id: undefined,
          });
        });
    } else {
      warehouseStore.clearWarehouseRacks();
      swageazyInventoryStore.setSampleReturnRequestFormData({
        current_rack: undefined,
        box_id: undefined,
      });
    }
  };

  const skuItems =
    swageazyInventoryStore.sampleReturnRequestFormData?.sku_items || [];
  const skuGroups = groupSkuItemsByProduct(skuItems);

  const canSubmit = () => {
    const formData = swageazyInventoryStore.sampleReturnRequestFormData;

    if (!formData?.order_id) return false;

    const sirOptions = swageazyInventoryStore.sampleReturnSirOptions;
    if (sirOptions.length > 0 && !formData?.stock_issue_request_id) {
      return false;
    }

    if (!formData?.warehouse_id && !formData?.warehouse_name) return false;
    if (!formData?.current_rack) return false;
    if (!formData?.box_id || formData.box_id.trim() === "") return false;

    const items = formData?.sku_items || [];
    if (items.length === 0) return false;

    return items.some(
      (item) => Boolean(item.selected) && Number(item.quantity) > 0,
    );
  };

  const clampReturnQty = (
    item: SwageazyInventorySkuLineItem,
    rawQty: number | undefined,
  ): number | undefined => {
    if (rawQty === undefined || Number.isNaN(rawQty)) return undefined;
    if (rawQty <= 0) return undefined;
    const pending = Number(item.pending_quantity);
    if (pending > 0 && rawQty > pending) return pending;
    return rawQty;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
      slotProps={{
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            minHeight: { xs: "30vh", sm: "28vh" },
            maxHeight: { xs: "90vh", sm: "85vh", md: "75vh", lg: "70vh" },
            maxWidth: {
              xs: "80vw",
              sm: "80vw",
              md: "70vw",
              lg: "60vw",
              xl: "50vw",
            },
            width: {
              xs: "80vw",
              sm: "80vw",
              md: "70vw",
              lg: "60vw",
              xl: "50vw",
            },
            margin: { xs: "16px", sm: "32px" },
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
          height: "100%",
          minHeight: { xs: "35vh", sm: "32vh" },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AssignmentReturnIcon
                sx={{
                  color: theme.palette.customColors.blueGradientStart,
                  fontSize: 24,
                  marginTop: 0.2,
                }}
              />
              <CustomTypography variant="heading3">
                {prefill?.lockSelection ? "Return sample stock" : "Sample Returned"}
              </CustomTypography>
            </Box>
            {prefill?.lockSelection ? (
              <CustomTypography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5, fontSize: "0.8125rem" }}
              >
                Order #{prefill.orderId} · SIR #{prefill.stockIssueRequestId}
              </CustomTypography>
            ) : null}
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.customColors.greyTextColor,
              "&:hover": {
                backgroundColor: theme.palette.customColors.lightGreyColor,
                color: theme.palette.customColors.blackBorder,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            padding: { xs: "20px", sm: "24px" },
            flex: 1,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <Grid2 container spacing={2} alignItems="stretch">
            {!prefill?.lockSelection ? (
              <>
            <Grid2
              size={{ xs: 12, sm: 6, md: 6 }}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <CustomTypography
                variant="heading6"
                style={{ fontWeight: 600, marginBottom: 16 }}
              >
                Order ID
                <span
                  style={{
                    color: theme.palette.error.main,
                    marginLeft: 4,
                  }}
                >
                  *
                </span>
              </CustomTypography>
              <AppSearchDropDown2
                title="Order ID"
                isMultiSelect={false}
                list={swageazyInventoryStore.orderSearchResults}
                value={
                  swageazyInventoryStore.sampleReturnRequestFormData?.order_id
                    ? [
                        {
                          id: String(
                            swageazyInventoryStore.sampleReturnRequestFormData
                              ?.order_id,
                          ),
                          value: String(
                            swageazyInventoryStore.sampleReturnRequestFormData
                              ?.order_id,
                          ),
                        },
                      ]
                    : []
                }
                handleSelect={handleOrderSelect}
                handleSearch={handleOrderSearch}
                local={false}
                minWidth="100%"
                borderRadius="8px"
              />
            </Grid2>

            {swageazyInventoryStore.sampleReturnSirOptions.length > 0 && (
              <Grid2
                size={{ xs: 12, sm: 6, md: 6 }}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 16 }}
                >
                  Stock Issue Request
                  <span
                    style={{
                      color: theme.palette.error.main,
                      marginLeft: 4,
                    }}
                  >
                    *
                  </span>
                </CustomTypography>
                <AppSearchDropDown2
                  title="Stock Issue Request"
                  isMultiSelect={false}
                  list={swageazyInventoryStore.sampleReturnSirOptions.map(
                    (sir: SampleSirReturnOption) => ({
                      id: String(sir.id),
                      value: `SIR #${sir.id} · pending ${sir.total_pending}/${sir.total_issued}`,
                    }),
                  )}
                  value={
                    swageazyInventoryStore.sampleReturnRequestFormData
                      ?.stock_issue_request_id
                      ? [
                          {
                            id: String(
                              swageazyInventoryStore
                                .sampleReturnRequestFormData
                                ?.stock_issue_request_id,
                            ),
                            value: `SIR #${swageazyInventoryStore.sampleReturnRequestFormData?.stock_issue_request_id}`,
                          },
                        ]
                      : []
                  }
                  handleSelect={handleSirSelect}
                  local={true}
                  minWidth="100%"
                  borderRadius="8px"
                />
              </Grid2>
            )}
              </>
            ) : (
              <Grid2 size={{ xs: 12 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.customColors.greyBorder}`,
                    backgroundColor:
                      theme.palette.customColors.lightGreyColor,
                  }}
                >
                  <Chip
                    label={`Order #${prefill?.orderId}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Chip
                    label={`SIR #${prefill?.stockIssueRequestId}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              </Grid2>
            )}
          </Grid2>

          {(prefill?.lockSelection ||
            (swageazyInventoryStore.sampleReturnRequestFormData?.order_id &&
              (swageazyInventoryStore.sampleReturnSirOptions.length === 0 ||
                swageazyInventoryStore.sampleReturnRequestFormData
                  ?.stock_issue_request_id))) && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <CustomTypography variant="heading6" fontWeight={600}>
                  Order SKUs{" "}
                  <span
                    style={{
                      color: theme.palette.error.main,
                      marginLeft: 4,
                    }}
                  >
                    *
                  </span>
                </CustomTypography>
                <CustomTypography
                  variant="body3"
                  style={{ color: theme.palette.text.secondary }}
                >
                  Select products / SKUs and enter qty for partial returns only.
                </CustomTypography>
              </Box>

              {skuGroups.length === 0 ? (
                <Box
                  sx={{
                    p: 3,
                    textAlign: "center",
                    color: theme.palette.customColors.greyTextColor,
                    border: `1px dashed ${theme.palette.customColors.greyBorder}`,
                    borderRadius: "8px",
                    backgroundColor: theme.palette.customColors.lightGreyColor,
                  }}
                >
                  <CustomTypography variant="body1">
                    No SKUs found for this order
                  </CustomTypography>
                </Box>
              ) : (
                skuGroups.map((group) => {
                  const selectedCount = group.items.filter(
                    (item) => item.selected,
                  ).length;
                  const allSelected =
                    group.items.length > 0 &&
                    selectedCount === group.items.length;
                  const someSelected =
                    selectedCount > 0 && selectedCount < group.items.length;

                  return (
                  <Box
                    key={group.productId}
                    sx={{
                      mb: 2,
                      p: 2,
                      border: `1px solid ${theme.palette.customColors.greyBorder}`,
                      borderRadius: "8px",
                      backgroundColor: theme.palette.customColors.lightGreyColor,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                        mb: 1.5,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={(e) => {
                              swageazyInventoryStore.setSampleReturnSkuItemsSelected(
                                group.items.map((item) => ({
                                  sku: item.sku,
                                  productId: item.product_id,
                                })),
                                e.target.checked,
                              );
                            }}
                          />
                        }
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            <CustomTypography variant="heading6" fontWeight={600}>
                              {group.productName}
                            </CustomTypography>
                            <Chip
                              size="small"
                              variant="outlined"
                              color="primary"
                              label={`${group.items.length} ${
                                group.items.length === 1 ? "SKU" : "SKUs"
                              }`}
                            />
                          </Box>
                        }
                        sx={{ mr: 0, alignItems: "center" }}
                      />
                    </Box>

                    {group.items.map((item) => (
                      <Box
                        key={`${item.product_id}-${item.sku}`}
                        sx={{
                          mb: 1.5,
                          p: { xs: 1.5, sm: 2 },
                          border: `1px solid ${theme.palette.customColors.greyBorder}`,
                          borderRadius: "8px",
                          backgroundColor: theme.palette.customColors.white,
                          opacity: item.selected ? 1 : 0.72,
                        }}
                      >
                        <Grid2
                          container
                          spacing={2}
                          sx={{ width: "100%", alignItems: "flex-start" }}
                        >
                          <Grid2
                            size={{ xs: 12, sm: 6, md: 6 }}
                            sx={{ minWidth: 0 }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 1,
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={Boolean(item.selected)}
                                onChange={(e) => {
                                  swageazyInventoryStore.updateSampleReturnSkuItem(
                                    item.sku,
                                    item.product_id,
                                    {
                                      selected: e.target.checked,
                                      quantity: e.target.checked
                                        ? item.quantity
                                        : undefined,
                                    },
                                  );
                                }}
                                sx={{ mt: -0.5, ml: -1 }}
                              />
                              <Box sx={{ minWidth: 0 }}>
                                <CustomTypography variant="body1" fontWeight={600}>
                                  {item.title || item.sku}
                                </CustomTypography>
                                <CustomTypography
                                  variant="body3"
                                  style={{
                                    color: theme.palette.text.secondary,
                                    wordBreak: "break-all",
                                  }}
                                >
                                  SKU: {item.sku}
                                </CustomTypography>
                                {item.expected_quantity !== undefined && (
                                  <CustomTypography
                                    variant="body3"
                                    style={{ color: theme.palette.text.secondary }}
                                  >
                                    Expected: {item.expected_quantity}
                                    {item.returned_quantity !== undefined
                                      ? ` · Returned: ${item.returned_quantity}`
                                      : ""}
                                    {item.pending_quantity !== undefined
                                      ? ` · Pending: ${item.pending_quantity}`
                                      : ""}
                                  </CustomTypography>
                                )}
                              </Box>
                            </Box>
                          </Grid2>
                          <Grid2
                            size={{ xs: 12, sm: 6, md: 6 }}
                            sx={{ minWidth: 0 }}
                          >
                            <CustomTextField
                              width="100%"
                              label="Qty Returned"
                              placeholder="Qty Returned"
                              value={item.quantity ?? ""}
                              disabled={!item.selected}
                              onChange={(e) =>
                                swageazyInventoryStore.updateSampleReturnSkuItem(
                                  item.sku,
                                  item.product_id,
                                  {
                                    quantity: clampReturnQty(
                                      item,
                                      e.target.value
                                        ? Number(e.target.value)
                                        : undefined,
                                    ),
                                  },
                                )
                              }
                              type="number"
                              allowNegativeValue={false}
                              allowDecimalValue={false}
                            />
                          </Grid2>
                        </Grid2>
                      </Box>
                    ))}
                  </Box>
                  );
                })
              )}
            </Box>
          )}

          <Divider sx={{ mb: 1 }} />
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, sm: 4, md: 4, lg: 4 }}>
              <CustomTypography
                variant="heading6"
                style={{ fontWeight: 600, marginBottom: 16 }}
              >
                Warehouse
                <span
                  style={{
                    color: theme.palette.error.main,
                    marginLeft: 4,
                  }}
                >
                  *
                </span>
              </CustomTypography>
              <AppSearchDropDown2
                isMultiSelect={false}
                title={"Select Warehouse"}
                local={true}
                backgroundColor={theme.palette.customColors.grayBackGroundColor}
                borderColor={theme.palette.customColors.greyBodyBackground}
                minWidth="13.938rem"
                list={
                  warehouseStore.warehouses?.map((item) => {
                    return { id: String(item?.id), value: String(item?.id) };
                  }) || []
                }
                value={
                  warehouseStore.warehouses?.length > 0 &&
                  (swageazyInventoryStore.sampleReturnRequestFormData
                    ?.warehouse_id ||
                    swageazyInventoryStore.sampleReturnRequestFormData
                      ?.warehouse_name)
                    ? (() => {
                        const warehouseId =
                          swageazyInventoryStore.sampleReturnRequestFormData
                            ?.warehouse_id;
                        const warehouseName =
                          swageazyInventoryStore.sampleReturnRequestFormData
                            ?.warehouse_name;

                        let warehouse: Warehouse | undefined = undefined;

                        if (warehouseId !== undefined && warehouseId !== null) {
                          warehouse =
                            warehouseStore.warehouses?.find(
                              (w) => String(w?.id) === String(warehouseId),
                            ) || undefined;
                        }

                        if (!warehouse && warehouseName) {
                          warehouse = warehouseStore.warehouses?.find(
                            (w) =>
                              String(w?.id) === warehouseName ||
                              w?.city_sku === warehouseName,
                          );
                        }

                        return warehouse
                          ? [
                              {
                                id: String(warehouse?.id),
                                value: String(warehouse?.id),
                              },
                            ]
                          : [];
                      })()
                    : []
                }
                handleSelect={(selectedItem: FilterValue[]) => {
                  if (selectedItem && selectedItem.length > 0) {
                    const warehouseUpdate: SwageazyInventorySampleReturnRequestFormData =
                      {};

                    warehouseUpdate.warehouse_id = selectedItem[0].id;
                    warehouseUpdate.warehouse_name = selectedItem[0].value;

                    swageazyInventoryStore.setSampleReturnRequestFormData(
                      warehouseUpdate,
                    );

                    handleWarehouseRackSearch(selectedItem[0].id);
                  } else {
                    swageazyInventoryStore.setSampleReturnRequestFormData({
                      warehouse_id: undefined,
                      warehouse_name: undefined,
                    });
                    handleWarehouseRackSearch(undefined);
                  }
                }}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 4, md: 4, lg: 4 }}>
              <CustomTypography
                variant="heading6"
                style={{ fontWeight: 600, marginBottom: 16 }}
              >
                Rack
                <span
                  style={{
                    color: theme.palette.error.main,
                    marginLeft: 4,
                  }}
                >
                  *
                </span>
              </CustomTypography>
              <AppSearchDropDown2
                title="Select Rack"
                list={
                  warehouseStore.warehouseRacks?.map((item) => {
                    return {
                      id: String(item?.id),
                      value: String(item?.id),
                    };
                  }) || []
                }
                value={
                  swageazyInventoryStore.sampleReturnRequestFormData
                    ?.current_rack
                    ? [
                        {
                          id: swageazyInventoryStore.sampleReturnRequestFormData
                            ?.current_rack,
                          value:
                            swageazyInventoryStore.sampleReturnRequestFormData
                              ?.current_rack,
                        },
                      ]
                    : []
                }
                handleSelect={(selectedItem: FilterValue[]) => {
                  if (selectedItem?.length > 0) {
                    const selectedRackId = selectedItem[0]?.id || "";
                    swageazyInventoryStore.setSampleReturnRequestFormData({
                      current_rack: selectedRackId,
                      box_id: undefined,
                    });
                  } else {
                    swageazyInventoryStore.setSampleReturnRequestFormData({
                      current_rack: undefined,
                      box_id: undefined,
                    });
                  }
                }}
                local={true}
                minWidth="100%"
                borderRadius="8px"
              />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 4, md: 4, lg: 4 }}>
              <CustomTypography
                variant="heading6"
                style={{ fontWeight: 600, marginBottom: 16 }}
              >
                Box
                <span
                  style={{
                    color: theme.palette.error.main,
                    marginLeft: 4,
                  }}
                >
                  *
                </span>
              </CustomTypography>
              <CustomTextField
                width="100%"
                placeholder="Enter Box ID"
                value={
                  swageazyInventoryStore.sampleReturnRequestFormData?.box_id ||
                  ""
                }
                onChange={(e) => {
                  swageazyInventoryStore.setSampleReturnRequestFormData({
                    box_id: e.target.value,
                  });
                }}
              />
            </Grid2>
          </Grid2>
        </DialogContent>

        <DialogActions
          sx={{
            padding: { xs: "10px 16px 10px", sm: "16px 20px 20px" },
            borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
            backgroundColor: theme.palette.customColors.lightGreyColor,
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <AppButton
            onClick={onClose}
            variant="grayscale"
            label="Cancel"
            customStyles={{
              borderColor: theme.palette.customColors.greyDarkborder,
              color: theme.palette.customColors.greyTextColor,
              fontWeight: 600,
              padding: "8px 20px",
              borderRadius: "8px",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-1px)",
              },
            }}
          />

          <AppButton
            onClick={onSave}
            variant="primary"
            label="Save"
            disabled={!canSubmit()}
            customStyles={{
              backgroundColor: canSubmit()
                ? theme.palette.customColors.EmeraldGreen
                : theme.palette.customColors.greyDarkborder,
              color: theme.palette.customColors.white,
              fontWeight: 600,
              padding: "8px 20px",
              borderRadius: "8px",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: canSubmit()
                  ? theme.palette.customColors.greenDark
                  : theme.palette.customColors.greyDarkborder,
                transform: canSubmit() ? "translateY(-1px)" : "none",
                boxShadow: canSubmit()
                  ? `0 4px 12px ${theme.palette.customColors.EmeraldGreen}30`
                  : "none",
              },
              "&:disabled": {
                backgroundColor: theme.palette.customColors.greyDarkborder,
                color: theme.palette.customColors.white,
                cursor: "not-allowed",
              },
            }}
          />
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default observer(SwageazyInventorySampleReturn);
